import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    TextField,
    Button,
    Alert,
    Stack,
    useMediaQuery,
    useTheme,
    Tooltip,
    Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PrintIcon from '@mui/icons-material/Print';
import ImageIcon from '@mui/icons-material/Image';
import ShareIcon from '@mui/icons-material/Share';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    closeShoppingList,
    removeFromShoppingList,
    updateQuantity,
    clearShoppingList,
} from '../../store/slices/shoppingListSlice';
import { ShoppingListItem, ShoppingListTotals } from '../../types/types';
import { themeColors, semanticColors } from '../../theme/colors';

// Компонент для версии печати
const PrintableShoppingList: React.FC<{
    items: ShoppingListItem[];
    totals: ShoppingListTotals;
}> = ({ items, totals }) => (
    <Box
        sx={{
            display: 'none',
            '@media print': {
                display: 'block',
                '& *': {
                    visibility: 'visible',
                },
            },
        }}
    >
        <style>
            {`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print-area, .print-area * {
                        visibility: visible;
                    }
                    .print-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    @page {
                        margin: 1cm;
                        size: A4;
                    }
                }
            `}
        </style>
        {/* печать */}
        <Box className="print-area" sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3, fontWeight: 600 }}>
                Список товаров для заказа
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Дата: {new Date().toLocaleDateString('ru-RU')}
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Код</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Название</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Кол-во</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Опт</TableCell>
                        {/* <TableCell align="right" sx={{ fontWeight: 600 }}>Сумма (Опт)</TableCell> */}
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Опт-Мастер</TableCell>
                        {/* <TableCell align="right" sx={{ fontWeight: 600 }}>Сумма (Опт-М)</TableCell> */}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>{item.kod}</TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.title}
                                </Typography>
                                {item.sub_title && (
                                    <Typography variant="caption" color="text.secondary">
                                        {item.sub_title}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="right">
                                {item.price_3} {item.currency === 'rub' ? '₽' : '$'}
                            </TableCell>
                            {/* <TableCell align="right">
                                {(item.price_3 * item.quantity).toFixed(2)} {item.currency === 'rub' ? '₽' : '$'}
                            </TableCell> */}
                            <TableCell align="right">
                                {item.price_4} {item.currency === 'rub' ? '₽' : '$'}
                            </TableCell>
                            {/* <TableCell align="right">
                                {(item.price_4 * item.quantity).toFixed(2)} {item.currency === 'rub' ? '₽' : '$'}
                            </TableCell> */}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #ddd' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="h6">
                            Общее количество: {totals.totalQuantity} шт.
                        </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="h6">
                            Итого (Опт): {totals.totalOpt.toFixed(2)} ₽
                        </Typography>
                        <Typography variant="h6">
                            Итого (Опт-Мастер): {totals.totalOptMaster.toFixed(2)} ₽
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    </Box>
);

const ShoppingListModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const { items, isOpen } = useAppSelector((state) => state.shoppingList);
    const [quantities, setQuantities] = useState<Record<number, number>>({});

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // Вычисляем итоги
    const totals: ShoppingListTotals = useMemo(() => {
        return items.reduce(
            (acc, item) => {
                const quantity = quantities[item.id] || item.quantity;
                return {
                    totalQuantity: acc.totalQuantity + quantity,
                    totalOpt: acc.totalOpt + (item.price_3 * quantity),
                    totalOptMaster: acc.totalOptMaster + (item.price_4 * quantity),
                };
            },
            { totalQuantity: 0, totalOpt: 0, totalOptMaster: 0 }
        );
    }, [items, quantities]);

    const handleClose = () => {
        dispatch(closeShoppingList());
    };

    const handleRemoveItem = (id: number) => {
        dispatch(removeFromShoppingList(id));
        delete quantities[id];
    };

    const handleQuantityChange = (id: number, value: string) => {
        const quantity = Math.max(1, parseInt(value) || 1);
        setQuantities(prev => ({ ...prev, [id]: quantity }));
        dispatch(updateQuantity({ id, quantity }));
    };

    const handleClearAll = () => {
        if (window.confirm('Вы уверены, что хотите очистить весь список заказа?')) {
            dispatch(clearShoppingList());
            setQuantities({});
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleShare = async () => {
        const shareText = `Список товаров для заказа:\n\n${items.map(item =>
            `${item.kod} - ${item.title} (${quantities[item.id] || item.quantity} шт.)`
        ).join('\n')}\n\nИтого: ${totals.totalOpt.toFixed(2)} ₽ (Опт) / ${totals.totalOptMaster.toFixed(2)} ₽ (Опт-Мастер)`;

        if (navigator.share && isMobile) {
            try {
                await navigator.share({
                    title: 'Список товаров для заказа',
                    text: shareText,
                });
            } catch (error) {
                console.log('Sharing cancelled');
            }
        } else {
            // Копируем в буфер обмена
            try {
                await navigator.clipboard.writeText(shareText);
                alert('Список скопирован в буфер обмена!');
            } catch (error) {
                console.error('Failed to copy:', error);
            }
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth={isMobile ? false : 'lg'}
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        backgroundColor: "#fff",
                        backgroundImage: 'none',
                        ...(isMobile && {
                            margin: 0,
                            borderRadius: 0,
                        }),
                    },
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    borderBottom: `1px solid ${semanticColors.border.light}`,
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Список заказа ({items.length})
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {items.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary" variant="h6">
                                Список заказа пуст
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 1 }}>
                                Добавьте товары из таблицы, нажав на иконку корзины
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Панель действий */}
                            <Box sx={{
                                p: 2,
                                borderBottom: `1px solid ${semanticColors.border.light}`,
                                backgroundColor: semanticColors.background.default,
                            }}>
                                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            startIcon={<PrintIcon />}
                                            onClick={handlePrint}
                                            variant="outlined"
                                            size="small"
                                        >
                                            Печать
                                        </Button>
                                        <Button
                                            startIcon={<ShareIcon />}
                                            onClick={handleShare}
                                            variant="outlined"
                                            size="small"
                                        >
                                            Поделиться
                                        </Button>
                                    </Stack>

                                    <Tooltip title="Очистить весь список">
                                        <Button
                                            startIcon={<ClearAllIcon />}
                                            onClick={handleClearAll}
                                            color="error"
                                            variant="outlined"
                                            size="small"
                                        >
                                            Очистить все
                                        </Button>
                                    </Tooltip>
                                </Stack>
                            </Box>

                            {/* Таблица товаров визуально*/}
                            <TableContainer sx={{ maxHeight: isMobile ? 'calc(100vh - 50px)' : '80vh' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ '& th': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}>
                                            <TableCell>Товар</TableCell>
                                            <TableCell align="center" sx={{ minWidth: 80 }}>Кол-во</TableCell>
                                            <TableCell align="right">Опт</TableCell>
                                            <TableCell align="right">Сумма (Опт)</TableCell>
                                            <TableCell align="right">Опт-М</TableCell>
                                            <TableCell align="right">Сумма (Опт-М)</TableCell>
                                            <TableCell align="center" width={50}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item) => {
                                            const quantity = quantities[item.id] || item.quantity;
                                            return (
                                                <TableRow key={item.id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {item.image ? (
                                                                <Avatar
                                                                    src={item.image}
                                                                    variant="rounded"
                                                                    sx={{ width: 40, height: 40, borderRadius: 1 }}
                                                                >
                                                                    <ImageIcon />
                                                                </Avatar>
                                                            ) : (
                                                                <Avatar
                                                                    variant="rounded"
                                                                    sx={{
                                                                        width: 40,
                                                                        height: 40,
                                                                        borderRadius: 1,
                                                                        bgcolor: themeColors['100'],
                                                                        color: themeColors['400']
                                                                    }}
                                                                >
                                                                    <ImageIcon />
                                                                </Avatar>
                                                            )}
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {item.title}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Код: {item.kod}
                                                                </Typography>
                                                                {item.sub_title && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        {item.sub_title}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={quantity}
                                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                            inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                                            sx={{ width: 70 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {item.price_3} {item.currency === 'rub' ? '₽' : '$'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                        {(item.price_3 * quantity).toFixed(2)} {item.currency === 'rub' ? '₽' : '$'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {item.price_4} {item.currency === 'rub' ? '₽' : '$'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                                        {(item.price_4 * quantity).toFixed(2)} {item.currency === 'rub' ? '₽' : '$'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Удалить из списка">
                                                            <IconButton
                                                                onClick={() => handleRemoveItem(item.id)}
                                                                size="small"
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Итоги */}
                            <Box sx={{
                                p: 2,
                                borderTop: `1px solid ${semanticColors.border.light}`,
                                backgroundColor: semanticColors.background.default,
                            }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Общее количество: {totals.totalQuantity} шт.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="flex-end">
                                            <Alert severity="info" sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Итого (Опт): {totals.totalOpt.toFixed(2)} ₽
                                                </Typography>
                                            </Alert>
                                            <Alert severity="success" sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Итого (Опт-Мастер): {totals.totalOptMaster.toFixed(2)} ₽
                                                </Typography>
                                            </Alert>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Компонент для печати */}
            <PrintableShoppingList items={items} totals={totals} />
        </>
    );
};

export default ShoppingListModal;